import type { Dispatch } from 'redux'
import endpoints from 'shared/store/endpoints'
import { ws } from 'shared/store/ws'
import type {
    CustomerAnalyticsData,
    LoginForm,
    OptinDTO
} from 'shared/types/api'
import { sendSigninForm } from 'shared/store/signin'
import type { SocialData } from 'shared/types'
import type { ActionUpdateToken } from 'shared/store/actions'
import type { CookiesConsents } from './types'
import type { ActionTransformPointsSuccess } from 'shared/store/kingdom/points'
import type { ActionProfileDataResponse } from 'shared/store/kingdom/profile'
import type {
    ActionSigninSendFormSuccess,
    ActionSigninWithTouchID,
    ActionSigninDone,
    ActionSigninPageDataResponse
} from 'shared/store/signin'
import type { ActionSignupPageDataResponse } from 'shared/store/signup'
import type { ActionRootPageResponse } from 'shared/store/root'
import type { ActionHomePageDataResponse } from 'shared/store/homepage'
import { logout, logoutDone } from 'shared/store/kingdom/dashboard'
import type { RootState } from 'shared/store/reducers'
import { decode as atob } from 'base-64'
import { errors, messages } from 'shared/utils'
import { boolean } from 'yup'

type GetState = () => RootState

export type ActionManageCookies = {
    type: 'MANAGE_COOKIES',
    payload: {
        show: boolean
    }
}

type ActionToggleTouchID = {
    type: 'TOGGLE_TOUCH_ID'
}

type ActionConsentAllCookies = {
    type: 'CONSENT_ALL_COOKIES'
}

type ActionDeclineAllCookies = {
    type: 'DECLINE_ALL_COOKIES'
}

type ActionAPIAuthError = {
    type: 'PAGE_API_AUTH_ERROR',
    page: string,
    error: any
}

type ActionConsentCookies = {
    type: 'CONSENT_COOKIES',
    payload: {
        consents: CookiesConsents
    }
}

type ActionTrackingStatus = {
    type: 'TRACKING_STATUS',
    trackingStatus: string
}

type ActionDeleteProfile = {
    type: 'PROFILE_DELETE_SUCCESS'
}

type ActionNewsletterSubscriptionError = {
    type: 'NEWSLETTER_SUBSCRIPTION_ERROR',
    error: any
}

type ActionNewsletterSubscriptionSuccess = {
    type: 'NEWSLETTER_SUBSCRIPTION_SUCCESS'
}

type ActionLogoutDone = {
    type: 'LOGOUT_DONE'
}

type ActionLogoutAfterError = {
    type: 'LOGOUT_AFTER_ERROR'
}

type ActionPreventPersistedError = {
    type: 'PREVENT_PERSISTED_ERROR'
}

type ActionSignupReset = {
    type: 'SIGNUP_RESET'
}

type ActionSignupSocialPending = {
    type: 'SOCIAL_PENDING',
    data: SocialData,
    fromLogin?: boolean
}

type ActionSignupSendFormConnected = {
    type: 'SIGNUP_SEND_FORM_CONNECTED',
    token: string
}

type ActionDeleteProfileSuccess = {
    type: 'PROFILE_DELETE_SUCCESS'
}

type ActionCloseInsert = {
    type: 'CLOSE_INSERT'
}

type ActionNotificationDisabled = {
    type: 'TOGGLE_NOTIFICATIONS_ENABLED',
    notificationsDisabled: boolean
}

type ActionResetToast = {
    type: 'RESET_TOAST'
}

export type ActionGetAnalyticsData = {
    type: 'USER_ANALYTICS_DATA',
    analyticsData: CustomerAnalyticsData
}

type ActionAllowGeofence = {
    type: 'ALLOW_GEOFENCE'
}

type ActionDenyGeofence = {
    type: 'DENY_GEOFENCE'
}

type ActionUpdateOptinPush = {
    type: 'UPDATE_OPTIN_PUSH',
    optinPush: boolean
}

const resetToast = (): ActionResetToast => ({
    type: 'RESET_TOAST'
})

const closeInsert = (): ActionCloseInsert => ({
    type: 'CLOSE_INSERT'
})

const consentAllCookies = (): ActionConsentAllCookies => ({
    type: 'CONSENT_ALL_COOKIES'
})

const declineAllCookies = (): ActionDeclineAllCookies => ({
    type: 'DECLINE_ALL_COOKIES'
})

const consentCookies = (consents: CookiesConsents): ActionConsentCookies => ({
    type: 'CONSENT_COOKIES',
    payload: {
        consents
    }
})

const displayManageCookies = (show: boolean): ActionManageCookies => ({
    type: 'MANAGE_COOKIES',
    payload: {
        show
    }
})

const changeTrackingStatus = (
    trackingStatus: string
): ActionTrackingStatus => ({
    type: 'TRACKING_STATUS',
    trackingStatus
})

const actionNewsletterSuccess = (): ActionNewsletterSubscriptionSuccess => ({
    type: 'NEWSLETTER_SUBSCRIPTION_SUCCESS'
})

const actionNewsletterError = (
    error: any
): ActionNewsletterSubscriptionError => ({
    type: 'NEWSLETTER_SUBSCRIPTION_ERROR',
    error
})

const socialPending = (data: SocialData): ActionSignupSocialPending => ({
    type: 'SOCIAL_PENDING',
    data
})

const toggleNotificationsEnabled = (
    notificationsDisabled: boolean
): ActionNotificationDisabled => ({
    type: 'TOGGLE_NOTIFICATIONS_ENABLED',
    notificationsDisabled
})

const toggleTouchID = (): ActionToggleTouchID => ({
    type: 'TOGGLE_TOUCH_ID'
})

const actionGetAnalyticsData = (
    analyticsData: CustomerAnalyticsData
): ActionGetAnalyticsData => ({
    type: 'USER_ANALYTICS_DATA',
    analyticsData
})

const getAnalyticsData = () => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { token } = getState().userState
        try {
            const response = await ws.get(endpoints.kingdom.analyticsData, {
                headers: token
                    ? { RenewToken: false, Authorization: token }
                    : {}
            })
            dispatch(actionGetAnalyticsData(response.data))
            return response.data
        } catch (error) {
            const { response } = error
            console.error('Error in analytics data', response)
        }
    }
}

const getTouchIdToken = () => {
    return async () => {
        try {
            const response = await ws.get(endpoints.kingdom.profile.touchid)
            return response.data
        } catch (error) {
            console.error('Error getting touch ID token :', error)
            return undefined
        }
    }
}

const sendNewsletterSubscription = (email: string) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            const response = await ws.post(endpoints.newsletter, {
                email: email
            })
            if (response && response.headers) {
                dispatch(actionNewsletterSuccess())
            }
        } catch (error) {
            const { response } = error
            console.error('Error sending newsletter subscription', response)
            dispatch(actionNewsletterError(error))
        }
    }
}

const onSocialPending = (
    data: LoginForm,
    fromLogin?: boolean,
    fromEcommerce?: boolean
) => async (dispatch: Dispatch<any>): Promise<?$Values<typeof messages>> => {
    if (fromLogin) {
        return (await dispatch(sendSigninForm(data, fromEcommerce))).message
    } else {
        await dispatch(
            socialPending({
                authenticated: false,
                token: undefined,
                userData: data
            })
        )
    }
}

const isAuthTokenExpired = () => {
    return async (dispatch: Dispatch<any>, getState: GetState) => {
        const { token } = getState().userState
        if (token) {
            try {
                const decodedToken = JSON.parse(atob(token.split('.')[1]))
                if (decodedToken.exp * 1000 < Date.now()) {
                    await dispatch(logout())
                    await dispatch(logoutDone({ isGlobal: false }))
                    return true
                }
            } catch (error) {
                console.error('Could not decode token', error)
            }
            return false
        }
    }
}

const preventPersistedError = (): ActionPreventPersistedError => ({
    type: 'PREVENT_PERSISTED_ERROR'
})

const logoutAfterError = (): ActionLogoutAfterError => ({
    type: 'LOGOUT_AFTER_ERROR'
})

const allowGeofence = (): ActionAllowGeofence => ({
    type: 'ALLOW_GEOFENCE'
})

const denyGeofence = (): ActionDenyGeofence => ({
    type: 'DENY_GEOFENCE'
})

const updateOptinPush = (optinPush: boolean): ActionUpdateOptinPush => ({
    type: 'UPDATE_OPTIN_PUSH',
    optinPush
})

const updateOptinPushBackend = (optinPush: boolean) => {
    return async (dispatch: Dispatch<any>) => {
        try {
            await ws.post(endpoints.optinPush, { push: optinPush })
            dispatch(getAnalyticsData())
            dispatch(updateOptinPush(optinPush))
        } catch (error) {
            const { response } = error
            console.error('Error updating profile optin push', response)
            throw error
        }
    }
}

export {
    consentAllCookies,
    declineAllCookies,
    consentCookies,
    displayManageCookies,
    changeTrackingStatus,
    actionNewsletterError,
    actionNewsletterSuccess,
    sendNewsletterSubscription,
    socialPending,
    onSocialPending,
    toggleNotificationsEnabled,
    closeInsert,
    resetToast,
    toggleTouchID,
    getTouchIdToken,
    getAnalyticsData,
    isAuthTokenExpired,
    logoutAfterError,
    preventPersistedError,
    allowGeofence,
    denyGeofence,
    updateOptinPush,
    updateOptinPushBackend
}

export type Action =
    | ActionConsentAllCookies
    | ActionDeclineAllCookies
    | ActionNotificationDisabled
    | ActionNewsletterSubscriptionSuccess
    | ActionNewsletterSubscriptionError
    | ActionConsentCookies
    | ActionManageCookies
    | ActionTrackingStatus
    | ActionUpdateToken
    | ActionSigninSendFormSuccess
    | ActionAPIAuthError
    | ActionDeleteProfile
    | ActionLogoutDone
    | ActionLogoutAfterError
    | ActionPreventPersistedError
    | ActionSignupReset
    | ActionSignupSocialPending
    | ActionSignupSendFormConnected
    | ActionDeleteProfileSuccess
    | ActionCloseInsert
    | ActionTransformPointsSuccess
    | ActionResetToast
    | ActionToggleTouchID
    | ActionSigninWithTouchID
    | ActionSigninDone
    | ActionRootPageResponse
    | ActionProfileDataResponse
    | ActionHomePageDataResponse
    | ActionGetAnalyticsData
    | ActionSigninPageDataResponse
    | ActionSignupPageDataResponse
    | ActionAllowGeofence
    | ActionDenyGeofence
    | ActionUpdateOptinPush
